import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { User } from '@/types/admin';
import { getRoleLabel } from '@/utils/roleUtils';
import { CUSTOMER_TYPE_ORGANIZATION } from '../utils/userManagementUtils';
import { JobTitle } from '@/types/api/auth';

export const CUSTOM_JOB_ROLE_VALUE = '__custom__';
export const JOB_ROLE_VALUES = ['의사', '간호사', '간호조무사', '응급구조사', '교육자', '학생', '연구원'] as const;
export const JOB_ROLE_TRANSLATION_KEYS: Record<(typeof JOB_ROLE_VALUES)[number], string> = {
    의사: 'doctor',
    간호사: 'nurse',
    간호조무사: 'nurseAide',
    응급구조사: 'paramedic',
    교육자: 'educator',
    학생: 'student',
    연구원: 'researcher',
};

// API JobTitle enum -> 한글 매핑
const JOB_TITLE_TO_KOREAN: Record<string, string> = {
    DOCTOR: '의사',
    NURSE: '간호사',
    NURSING_AIDE: '간호조무사',
    PARAMEDIC: '응급구조사',
    EDUCATOR: '교육자',
    STUDENT: '학생',
    RESEARCHER: '연구원',
};

function resolveJobRoleSelection(jobTitle?: string, customJobTitle?: string): { jobRole: string; customJobRole: string } {
    if (!jobTitle) return { jobRole: '', customJobRole: '' };
    
    // CUSTOM인 경우
    if (jobTitle === 'CUSTOM' || jobTitle === JobTitle.CUSTOM) {
        return { jobRole: CUSTOM_JOB_ROLE_VALUE, customJobRole: customJobTitle || '' };
    }
    
    // API enum 값을 한글로 변환
    const koreanJobTitle = JOB_TITLE_TO_KOREAN[jobTitle];
    if (koreanJobTitle) {
        return { jobRole: koreanJobTitle, customJobRole: '' };
    }
    
    // 알 수 없는 값은 커스텀으로 처리
    return { jobRole: CUSTOM_JOB_ROLE_VALUE, customJobRole: jobTitle };
}

export interface EditUserFormData {
    name: string;
    email: string;
    role: string;
    accountType?: string;
    status: string;
    password?: string;
    expirationDate?: string;
    organizationId?: number;
    organizationName?: string;
    affiliation?: string;
    country?: string;
    customerType?: string;
    phoneNumber?: string;
    position?: string;
    department?: string;
}

interface UseEditUserFormParams {
    user: User | null;
    onSave: (user: User) => void | Promise<void>;
    onClose: () => void;
}

const initialFormData: EditUserFormData = {
    name: '',
    email: '',
    role: 'Student',
    accountType: '',
    status: 'active',
    password: '',
    expirationDate: '',
    organizationId: 3,
    organizationName: '',
    affiliation: '',
    country: '',
    customerType: '',
    phoneNumber: '',
    position: '',
    department: ''
};

export function useEditUserForm({ user, onSave, onClose }: UseEditUserFormParams) {
    const { t } = useTranslation(['users', 'auth']);
    const [formData, setFormData] = useState<EditUserFormData>(initialFormData);
    const [jobRole, setJobRole] = useState<string>('');
    const [customJobRole, setCustomJobRole] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isGuest = user?.role === 'Guest' || user?.accountType === '게스트' || user?.type === 'guest';
    const isOrganizationCustomer = formData.customerType === CUSTOMER_TYPE_ORGANIZATION || formData.customerType === '기관고객';
    const isAdminCustomer = formData.customerType?.toLowerCase() === 'admin';
    const displayCustomerType = isAdminCustomer
        ? t('superAdmin', { defaultValue: '슈퍼관리자' })
        : formData.customerType || '-';
    const displayLicenseOrganization = isOrganizationCustomer
        ? formData.organizationName || t('customerTypeOrg')
        : isAdminCustomer
            ? displayCustomerType
            : t('personalUser', { defaultValue: '개인사용자' });
    const roleDisplay = getRoleLabel(String(formData.role ?? '').toLowerCase());
    const displayRoleBadge = formData.accountType
        ? `${roleDisplay} / ${formData.accountType}`
        : roleDisplay;

    useEffect(() => {
        if (user) {
            const nextJobRole = resolveJobRoleSelection(user.jobTitle, user.customJobTitle);
            // department는 레거시 호환을 위해 jobRole 한글값 또는 customJobRole로 저장
            const department = nextJobRole.jobRole === CUSTOM_JOB_ROLE_VALUE 
                ? nextJobRole.customJobRole 
                : nextJobRole.jobRole;
            
            setFormData({
                name: user.name || '',
                email: user.email || '',
                role: user.role || 'Student',
                accountType: user.accountType || '',
                status: user.status || 'active',
                password: '',
                expirationDate: user.expirationDate || '',
                organizationId: 3,
                organizationName: user.organizationName || '',
                affiliation: user.affiliation || user.organization || '',
                country: user.country || '',
                customerType: user.customerType || '',
                phoneNumber: user.phoneNumber || '',
                position: user.position || '',
                department
            });
            setJobRole(nextJobRole.jobRole);
            setCustomJobRole(nextJobRole.customJobRole);
        }
    }, [user]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleJobRoleChange = (value: string) => {
        setJobRole(value);
        if (value === CUSTOM_JOB_ROLE_VALUE) {
            setFormData(prev => ({ ...prev, department: customJobRole }));
            return;
        }
        setCustomJobRole('');
        setFormData(prev => ({ ...prev, department: value }));
    };

    const handleCustomJobRoleChange = (value: string) => {
        setCustomJobRole(value);
        setFormData(prev => ({ ...prev, department: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || isSubmitting) return;
        try {
            setIsSubmitting(true);
            await onSave({ ...user, ...formData } as User);
        } catch {
            // Parent handlers already surface the error alert; close the modal so the alert is not hidden behind it.
        } finally {
            setIsSubmitting(false);
            onClose();
        }
    };

    return {
        customJobRole,
        displayLicenseOrganization,
        displayRoleBadge,
        formData,
        handleCustomJobRoleChange,
        handleInputChange,
        handleJobRoleChange,
        handleSubmit,
        isGuest,
        isOrganizationCustomer,
        isSubmitting,
        jobRole,
        setFormData,
    };
}
